import type { Messages } from '../de';

export default {
  plural: {
    run: 'runs',
    ride: 'rides',
    swim: 'swims',
    yoga: 'yoga sessions',
    strength: 'strength sessions',
    hike: 'hikes',
    walk: 'walks',
    other: 'sessions',
    all: 'activities',
  },
  title: 'Analysis',
  kicker: 'Analysis · {name}',
  pointPerRun: 'One dot per run.',
  pointPerUnit: 'One dot per session.',
  allSports: 'All sports',
  sportGroup: 'Sport',
  all: 'All',
  loadError: 'Could not load: {error}',
  noActivities: 'No activities yet. Analysis fills up after the first sync or FIT upload.',
  noSpeed: '{name} has no pace – the pace cloud and efficiency need a sport with speed.',
  noSpeedAll: 'All sports together have no common pace – the pace cloud and efficiency are shown per sport.',
  desktopHint: 'Pace cloud, fingerprints and “What does a session cost?” are on desktop',
  trend: 'Trend',
  noHr: 'no HR',
  ofN: '{a} of {b}',
  pace: {
    titleRun: 'Every run: pace over time',
    titlePace: 'Every session: pace over time',
    titleSpeed: 'Every session: speed over time',
    sub: 'Dot size and opacity = duration · colour = zone of avg HR · line = rolling trend (±45 days)',
    subFaster: ' · faster is up',
    label: 'Pace over time',
    chartLabel: 'Scatter of {count} sessions',
    chartTrend: '; trend {trend}',
    empty: 'No sessions with pace.',
    since: '{from} → {to} {unit} since {date} – {verdict}',
    same: 'about the same',
    faster: 'faster',
    slower: 'slower',
    takeaway: 'Trend: ',
  },
  eff: {
    title: 'Efficiency index',
    sub: 'Metres per heartbeat = avg speed × 60 / avg HR · higher is fitter',
    unit: 'm/beat',
    unitName: 'Session',
    chartLabel: 'Efficiency over time, {change}',
    empty: 'No sessions with pace and heart rate.',
    last90: 'Last 90 days: ',
    vsYear: ' vs {prev} a year ago – {change}',
  },
  load: {
    fitnessSeries: 'Fitness (CTL)',
    fatigueSeries: 'Fatigue (ATL)',
    formSeries: 'Form (TSB)',
    title: 'Fitness · Fatigue · Form',
    sub: 'all sports · CTL 42 days, ATL 7 days, form = CTL − ATL · last 12 months',
    formBig: 'Form {value}',
    chartLabel: 'Fitness, fatigue and form over 12 months; form {form}, {word}',
    races: '; races: {list}',
    raceItem: '{title} on {date}',
    empty: 'No training load yet.',
    fitness: 'Fitness',
    fatigue: 'Fatigue',
    form: 'Form',
    race: 'Race ({count})',
    estimated: {
      one: 'includes ~{count} estimated load (no heart rate)',
      other: 'includes ~{count} estimated loads (no heart rate)',
    },
  },
  cost: {
    title: 'What does a session cost?',
    subHrv: 'Daily load (hrTSS, all sessions of the day) vs HRV change the next morning – next night minus the 7 nights before · hollow = estimated (~)',
    subRhr: 'Daily load (hrTSS, all sessions of the day) vs resting HR change the next morning – next day minus the 7 days before · hollow = estimated (~)',
    metric: 'Recovery measure',
    hrv: 'HRV',
    rhr: 'Resting HR',
    unitHrv: 'ms',
    unitRhr: 'bpm',
    load: 'hrTSS',
    chartLabel: 'Scatter of daily load vs next-day {metric} change for {count} training days',
    empty: 'No training days with load and {metric} of the following night yet.',
    tooltip: '{load} hrTSS · {metric} {delta} {unit} (next night {next}, average {base})',
    sessions: { one: '{count} session', other: '{count} sessions' },
    trend: 'Trend line',
    zone: 'from ~{threshold} hrTSS',
    tipHrv: 'From ~{threshold} hrTSS, next-day HRV drops by {value} ms on average',
    tipRhr: 'From ~{threshold} hrTSS, next-day resting HR rises by {value} bpm on average',
    noTip: 'No clear effect: even after days from ~{threshold} hrTSS, {metric} changes by only {value} {unit} on average',
    days: ' ({count} days)',
    rest: '; after rest days {value} {unit}.',
    perHundred: ' Trend: {value} {unit} per 100 hrTSS.',
    few: 'Not enough hard days for a statement yet.',
  },
  prints: {
    aria: '{sport} {dist} on {date}',
    fadeAria: ', fade in the second half',
    fade: 'Fade 2nd half',
    even: 'even',
    hr: 'HR',
    titleRun: 'Run fingerprints',
    title: 'Fingerprints',
    lastN: 'last 12 {what}',
    recent: 'long runs from {km} km, the last 12',
    longest: 'the 12 longest runs – not yet 12 long runs from {km} km',
    sub: '{which} · pace over distance relative to your own average (up = faster), area = elevation · red and “fade” = second half ≥ 4% slower',
    fadeAt: 'Fade in ',
    empty: 'No {what} with streams.',
  },
  z2: {
    title: 'Pace at Z2 heart rate',
    sub: 'Median pace per week from running minutes in Z2 ({band} bpm) · without the first 10 min, walking and stops · GPS runs only, weeks with {min} min or more · faster is up',
    subNoBand: 'Median pace per week from running minutes in Z2 · without the first 10 min, walking and stops · faster is up',
    week: 'Week (size = minutes in Z2)',
    tooltip: 'Week of {week}<br/>{pace} /km · {min} min in Z2 · avg {hr} bpm',
    chartLabel: 'Pace at Z2 heart rate over {count} weeks; {trend}',
    empty: 'No week with at least {min} running minutes in Z2 yet.',
    noZones: 'Without heart rate zones there is no Z2 band. Zones are in the settings.',
    last12Short: 'last 12 weeks',
    last12: 'Last 12 weeks ({min} min in Z2): ',
    vsYear: ' against {pace} /km a year earlier – {change}',
    faster: '{s} s/km faster',
    slower: '{s} s/km slower',
    same: 'about the same',
  },
  zones: {
    title: 'Time in zones per week',
    sub: 'last 16 weeks',
    subShare: 'last 16 weeks · {share}% in Z{zone}',
    grey: ' – the “grey zone”',
    empty: 'No zone times in this period.',
    chartLabel: 'Stacked time in heart rate zones per week, {share} percent in zone {zone}',
    barsLabel: 'Time in zones over 16 weeks, {share} percent in zone {zone}',
    noVersion: 'no zone version in this period',
    withoutHr: ' · {count} without HR',
    version: 'Zones v{version} · valid from {date}',
  },
  cov: {
    title: 'Data coverage',
    sub: 'months × source · what’s missing? · darker = more complete',
    empty: 'No data yet.',
    aria: '{label} ({source}): {total} in total, {empty} of {months} months empty',
    days: ' days',
  },
  hours: {
    title: 'Hours per month',
    sub: 'by sport, stacked · last 12 months',
    perMonth: 'per month',
    empty: 'No training time in the last 12 months.',
    chartLabel: 'Stacked bars: training hours per month by sport, most in {month} with {hours} hours',
  },
} satisfies Messages['analysis'];
